// Pokémon Type and Region taxonomy + helpers for v5.

export type PokeType =
  | "normal" | "fire" | "water" | "electric" | "grass" | "ice"
  | "fighting" | "poison" | "ground" | "flying" | "psychic" | "bug"
  | "rock" | "ghost" | "dragon" | "dark" | "steel" | "fairy";

/**
 * The 18 types. Each is drawn with our OWN type icon: a disc in the type's
 * colour carrying a white glyph of our design (client/public/type-icons/
 * <key>.svg, 2026-09-10). The previous set was Pokémon GO's own type discs
 * copied from the PokeMiners asset dump; those are Scopely-owned game assets
 * and were removed in the IP review (docs/ip-audit.md). Same disc-and-glyph
 * grammar, so a type still reads at a glance next to the game, but every
 * pixel is ours. See typeIconSrc and components/TypeIcon.
 */
export const ALL_TYPES: { key: PokeType; label: string; color: string }[] = [
  { key: "normal",   label: "Normal",   color: "#A8A878" },
  { key: "fire",     label: "Fire",     color: "#F08030" },
  { key: "water",    label: "Water",    color: "#6890F0" },
  { key: "electric", label: "Electric", color: "#F8D030" },
  { key: "grass",    label: "Grass",    color: "#78C850" },
  { key: "ice",      label: "Ice",      color: "#98D8D8" },
  { key: "fighting", label: "Fighting", color: "#C03028" },
  { key: "poison",   label: "Poison",   color: "#A040A0" },
  { key: "ground",   label: "Ground",   color: "#E0C068" },
  { key: "flying",   label: "Flying",   color: "#A890F0" },
  { key: "psychic",  label: "Psychic",  color: "#F85888" },
  { key: "bug",      label: "Bug",      color: "#A8B820" },
  { key: "rock",     label: "Rock",     color: "#B8A038" },
  { key: "ghost",    label: "Ghost",    color: "#705898" },
  { key: "dragon",   label: "Dragon",   color: "#7038F8" },
  { key: "dark",     label: "Dark",     color: "#705848" },
  { key: "steel",    label: "Steel",    color: "#B8B8D0" },
  { key: "fairy",    label: "Fairy",    color: "#EE99AC" },
];

/** Our bundled icon for a type (SVG, crisp at every size the app uses). */
export function typeIconSrc(type: PokeType): string {
  const base = (typeof import.meta !== "undefined" && import.meta.env?.BASE_URL) || "/";
  return `${base}type-icons/${type}.svg`;
}

/** Whether a string is one of the 18 type keys (OCR and feed data arrive as strings). */
export function isPokeType(v: unknown): v is PokeType {
  return typeof v === "string" && ALL_TYPES.some((t) => t.key === v);
}

export type Region =
  | "kanto" | "johto" | "hoenn" | "sinnoh" | "unova" | "kalos"
  | "alola" | "galar" | "hisui" | "paldea" | "unidentified";

export const REGIONS: { key: Region; label: string; range: [number, number] }[] = [
  { key: "kanto",  label: "Kanto",  range: [1, 151] },
  { key: "johto",  label: "Johto",  range: [152, 251] },
  { key: "hoenn",  label: "Hoenn",  range: [252, 386] },
  { key: "sinnoh", label: "Sinnoh", range: [387, 493] },
  { key: "unova",  label: "Unova",  range: [494, 649] },
  { key: "kalos",  label: "Kalos",  range: [650, 721] },
  { key: "alola",  label: "Alola",  range: [722, 809] },
  // Galar covers 810..905 EXCEPT the 7 Hisui-evolution dex numbers
  // (899..905), which we attribute to Hisui below via HISUI_DEX_IDS so
  // they no longer fall through to the catch-all "Unknown" bucket.
  { key: "galar",  label: "Galar",  range: [810, 898] },
  // Hisui is an explicit id-set (the #899..905 evolution line), not a
  // contiguous range — see HISUI_DEX_IDS in pogoAvailability.ts. The range
  // here mirrors the set's bounds for display/reference only.
  { key: "hisui",  label: "Hisui",  range: [899, 905] },
  { key: "paldea", label: "Paldea", range: [906, 1025] },
  // "Unidentified" is the in-game GO grouping for Meltan #808 / Melmetal #809 —
  // species that carry no region despite sitting inside Alola's dex range. Kept
  // LAST so they render at the very end of the dex, after Paldea, rather than
  // mid-list at their dex number. regionForId() attributes 808/809 here via the
  // UNIDENTIFIED_REGION_IDS carve-out below (same shape as the Hisui carve-out).
  { key: "unidentified", label: "Unidentified", range: [808, 809] },
];

// Hisui-evolution dex numbers, attributed to the Hisui region in
// regionForId(). Local copy to keep types.ts free of a runtime import of
// pogoAvailability (which imports the variant classifier); the canonical
// list lives in pogoAvailability.HISUI_DEX_IDS and the two are asserted
// equal in pogoAvailability.test.ts.
const HISUI_REGION_IDS = new Set<number>([899, 900, 901, 902, 903, 904, 905]);

/**
 * Species filed under the "Unidentified" region in Pokémon GO, despite what
 * their dex number implies.
 *
 * Meltan #808 / Melmetal #809 sit inside Alola's 722..809 range by dex number,
 * but they are not Alolan — GO files them under an "Unidentified" region, so the
 * in-game Alola count is 2 lower than a range check produces. Attributing them
 * to Alola made our region counts disagree with the game, so they get their own
 * region that renders at the very end of the dex (after Paldea).
 *
 * Same carve-out shape as HISUI_REGION_IDS above, which pulls #899..905 out of
 * Galar's range — a dex range is an approximation of region, not the truth.
 */
const UNIDENTIFIED_REGION_IDS = new Set<number>([808, 809]);

export function regionForId(id: number): Region | null {
  if (UNIDENTIFIED_REGION_IDS.has(id)) return "unidentified";
  if (HISUI_REGION_IDS.has(id)) return "hisui";
  for (const r of REGIONS) {
    if (r.key === "hisui" || r.key === "unidentified") continue;
    if (id >= r.range[0] && id <= r.range[1]) return r.key;
  }
  return null;
}

export const TYPE_INFO: Record<PokeType, { label: string; color: string }> =
  Object.fromEntries(ALL_TYPES.map(t => [t.key, t])) as any;

// Map of canonical primary/secondary types for the Pokémon present in
// the curated POKEMON dataset (~120 entries). Anything not listed defaults to "normal".
export const POKE_TYPES: Record<number, [PokeType, PokeType?]> = {
  // Gen 1
  1: ["grass", "poison"], 4: ["fire"], 6: ["fire", "flying"], 7: ["water"],
  25: ["electric"], 26: ["electric"], 39: ["normal", "fairy"], 52: ["normal"],
  65: ["psychic"], 94: ["ghost", "poison"], 130: ["water", "flying"],
  131: ["water", "ice"], 133: ["normal"], 143: ["normal"],
  144: ["ice", "flying"], 145: ["electric", "flying"], 146: ["fire", "flying"],
  149: ["dragon", "flying"], 150: ["psychic"], 151: ["psychic"],
  // Gen 2
  152: ["grass"], 155: ["fire"], 158: ["water"], 175: ["fairy"],
  196: ["psychic"], 197: ["dark"], 212: ["bug", "steel"], 245: ["water"],
  248: ["rock", "dark"], 249: ["psychic", "flying"], 250: ["fire", "flying"],
  251: ["psychic", "grass"],
  // Gen 3
  252: ["grass"], 255: ["fire"], 258: ["water"], 280: ["psychic", "fairy"],
  282: ["psychic", "fairy"], 302: ["dark", "ghost"], 359: ["dark"],
  380: ["dragon", "psychic"], 381: ["dragon", "psychic"], 384: ["dragon", "flying"],
  385: ["steel", "psychic"], 386: ["psychic"],
  // Gen 4
  387: ["grass"], 390: ["fire"], 393: ["water"], 445: ["dragon", "ground"],
  448: ["fighting", "steel"], 482: ["psychic"], 483: ["steel", "dragon"],
  484: ["water", "dragon"], 487: ["ghost", "dragon"], 489: ["water"],
  490: ["water"], 491: ["dark"], 493: ["normal"],
  // Gen 5
  495: ["grass"], 498: ["fire"], 501: ["water"], 570: ["dark"], 571: ["dark"],
  642: ["electric", "flying"], 643: ["dragon", "fire"], 644: ["dragon", "electric"],
  646: ["dragon", "ice"], 647: ["water", "fighting"], 648: ["normal", "psychic"],
  649: ["bug", "steel"],
  // Gen 6
  650: ["grass"], 653: ["fire"], 656: ["water"], 700: ["fairy"],
  716: ["fairy"], 717: ["dark", "flying"], 718: ["dragon", "ground"],
  719: ["rock", "fairy"], 720: ["psychic", "ghost"], 721: ["fire", "water"],
  // Gen 7
  722: ["grass", "flying"], 725: ["fire"], 728: ["water"], 745: ["rock"],
  746: ["water"], 764: ["fairy"], 771: ["water"], 778: ["ghost", "fairy"],
  785: ["electric", "fairy"], 786: ["psychic", "fairy"], 787: ["grass", "fairy"],
  788: ["water", "fairy"], 789: ["psychic"], 791: ["psychic", "steel"],
  792: ["psychic", "ghost"], 793: ["rock", "poison"], 794: ["bug", "fighting"],
  795: ["bug", "fighting"], 796: ["electric"], 797: ["steel", "flying"],
  798: ["grass", "steel"], 799: ["dark", "dragon"], 800: ["psychic"],
  801: ["steel", "fairy"], 802: ["fighting", "ghost"], 803: ["poison"],
  805: ["rock", "steel"], 806: ["fire", "ghost"], 807: ["electric"],
  // Gen 8
  810: ["grass"], 813: ["fire"], 816: ["water"], 849: ["electric", "poison"],
  887: ["dragon", "ghost"], 888: ["fairy"], 889: ["fighting"],
  890: ["poison", "dragon"], 891: ["fighting"], 892: ["fighting"],
  893: ["dark", "grass"], 894: ["electric"], 895: ["dragon"],
  896: ["ice"], 897: ["ghost"], 898: ["psychic", "grass"],
  // Gen 9
  906: ["grass"], 909: ["fire"], 912: ["water"], 957: ["fairy", "steel"],
  958: ["fairy", "steel"], 959: ["fairy", "steel"], 977: ["water"],
  998: ["dragon", "ice"], 1000: ["steel", "ghost"],
  1007: ["fighting", "dragon"], 1008: ["electric", "dragon"], 1024: ["normal"],
};

// Full National Dex type table (1..1025), generated from canonical
// main-series default-form typings. This is the authoritative source for
// the Dex type filter; POKE_TYPES above is kept only as a small curated
// override layer for any id we want to special-case. See
// client/src/data/speciesTypes.ts.
import { SPECIES_TYPES } from "../data/speciesTypes";

export function typesFor(id: number): PokeType[] {
  // Curated override wins (lets us hand-tune specific ids), then the full
  // generated table, then a defensive "normal" fallback for stray ids.
  const override = POKE_TYPES[id];
  if (override) return override.filter(Boolean) as PokeType[];
  const full = SPECIES_TYPES[id];
  if (full) return full.filter(Boolean) as PokeType[];
  return ["normal"];
}
